<template>
    <div class="sf__results">
        <!-- <h1>{{ vehicle.collection_name }}</h1> -->

        <div v-if="results.length < 1">
            <p>There are no results matching your search. Please try modifying filters to find matching products.</p>
        </div>

        <!-- So the first filter is tabs. ... the second filter is the one that goes in -->
        <template v-else>
            <div class="sf__results__alerts" v-if="Object.values(alerts).filter(a => a).length > 0 && foam">
                <template v-for="(alert, key) in alerts" :key="key">
                    <div v-if="alert" class="sf__result__alert">
                        <img class="sf__result__alert__image" v-if="alert.image" :src="alert.image">
                        <div class="sf__result__alert__content">
                            <p>{{ alert.label }}</p>
                            <p class="sf__result__alert__mesage" v-html="alert.message"></p>
                        </div>
                    </div>
                </template>
            </div>


            <div class="sf__results__tabs" :class="{ 'sf__results__tabs--foam-only': foamOnly }">

                <!-- Tabs List -->
                <div class="sf__result__tabs__navigation">
                    <button
                        v-if="tabs_overflow && current_tab > 0"
                        type="button"
                        class="sf__result__tabs__arrow sf__result__tabs__arrow--left"
                        aria-label="Select previous row"
                        @click="scrollTabs('left')"
                    >
                        <svg width="8" height="12" viewBox="0 0 8 12" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                            <path d="M5.707 -2.49461e-07L7.121 1.414L2.828 5.707L7.121 10L5.707 11.414L2.49461e-07 5.707L5.707 -2.49461e-07Z" fill="black"/>
                        </svg>
                    </button>

                    <div
                        ref="tabsList"
                        class="sf__result__tabs__list"
                        @scroll.passive="updateTabNavigation"
                    >
                        <button type="button" v-for="(tab, i) in tabs" :key="i" v-bind:class="{ 'active': current_tab == i }" @click="current_tab = i">
                            {{ formatPlpTabLabel(tab) }}
                        </button>
                    </div>

                    <button
                        v-if="tabs_overflow && current_tab < tabs.length - 1"
                        type="button"
                        class="sf__result__tabs__arrow sf__result__tabs__arrow--right"
                        aria-label="Select next row"
                        @click="scrollTabs('right')"
                    >
                        <svg width="8" height="12" viewBox="0 0 8 12" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                            <path d="M1.41409 -2.49461e-07L9.38751e-05 1.414L4.29309 5.707L9.34998e-05 10L1.41409 11.414L7.12109 5.707L1.41409 -2.49461e-07Z" fill="black"/>
                        </svg>
                    </button>
                </div>

                <!-- Shortcuts within each tab. -->
                <template v-for="(tab, i) in tabs" :key="i">
                    <div v-if="!pulling_plaform_results && filterResults({row: tab}).length > 0 && i == current_tab">

                        <!-- And here we add the quick access if the level 1 filter values is greater than 1. -->
                        <div
                            class="sf__result__shortcuts"
                            :class="{ 'sf__result__shortcuts--single': getSides(filterResults({row: tab}), tab).length === 1 }"
                        >
                            <template v-for="(side, j) in getSides(filterResults({row: tab}), tab)" :key="j">
                                <a
                                    :href="`#filter-${i}-${j}`"
                                    v-if="filterResults({row: tab, side: side}).length > 0"
                                    @click.prevent="scrollToResultSection(`filter-${i}-${j}`)"
                                >{{ formatPlpLabel(side) }}</a>
                            </template>
                        </div>

                        <FoamOnlyNotice v-if="foamOnly" :vehicle-params="vehicleParams" />

                        <!-- This is basically the tab -->
                        <template v-for="(side, j) in getSides(filterResults({row: tab}), tab)" :key="j">
                            <div v-if="filterResults({row: tab, side: side}).length > 0">
                                <h4 class="sf__result__section__title" :id="`filter-${i}-${j}`">{{ formatPlpHeading(tab, side) }}</h4>
                                <ResultsList :parts="filterResults({
                                    row: tab, 
                                    side: side
                                })" />
                            </div>
                        </template>
                    </div>
                </template>
            </div>
        </template>
    </div>
</template>

<script>
import ResultsList from './ResultsList.vue'; 
import store from '../store'; 
import Api from '../helpers/api'; 
import Storage from '../helpers/storage';
import Ecommerce from '../adapters/Ecommerce'; 
import FoamOnlyNotice from './FoamOnlyNotice.vue';

const Platform = Ecommerce.getInstance(window.sf_options.platform); 


export default {
    props: ['results', 'vehicle', 'foam', 'foamOnly', 'vehicleParams'],

    components: {
        ResultsList, 
        FoamOnlyNotice,
    }, 

    watch: {
        results( newVal ) {
            // We store this so we can recommend after. 
            store.results_page = newVal;
            Storage.storeRecommendationContext(newVal);
            this.updateProductData(); 
           
            // if there's less tabs than current_tab was set to, set it to 0
            if(this.tabs.length <= 1) {
                this.current_tab = 0;
            }

            this.$nextTick(this.updateTabNavigation);
        }
    }, 

    mounted() {
        // We store this so we can recommend after. 
        store.results_page = this.results;
        Storage.storeRecommendationContext(this.results);
        this.updateProductData(); 
        this.$nextTick(this.setupTabNavigation);
    }, 

    beforeUnmount() {
        window.removeEventListener('resize', this.updateTabNavigation);

        if (this.tabs_resize_observer) {
            this.tabs_resize_observer.disconnect();
        }
    },


    computed: {
        alerts() {
            return store.alerts; 
        },
        tabs () {
            return this.getRows(this.results)
        }
    }, 


    data() {
        return {
            current_tab: 0, 
            filters: [], 
            filter_tabs: null, 
            filter_level_1: null, 
            active_tab: 1,
            product_data: {},
            scroll_adjust_timeout: null,
            tabs_overflow: false,
            tabs_resize_observer: null,

            // We use this flag to know if this is pulling results from 
            // The CRM platform before showing, so we show everything 
            // after we have all the information.
            pulling_plaform_results: false
        }
    }, 

    methods: {

        getValuesOrder(slug) {
            return store.filters.find( filter => filter.slug == slug )
                ?.values.map( value => value.value ) || [];
        }, 

        getRows(parts) {
            return parts.reduce((carry, res) => {
                if ( !carry.includes(res.row) ) carry.push(res.row); 
                return carry;
            }, []).sort((a, b) => {
                const rowDifference = this.getRowPosition(a) - this.getRowPosition(b);
                if (rowDifference) return rowDifference;

                // Bench/split variants precede bucket seats within the same row.
                const bucketDifference = Number(/bucket/i.test(a)) - Number(/bucket/i.test(b));
                return bucketDifference || String(a).localeCompare(String(b), 'en', {numeric: true});
            });
        },

        getRowPosition(row) {
            const label = String(row || '').trim().toLowerCase();
            const positions = {front: 1, first: 1, second: 2, third: 3, fourth: 4};
            const namedRow = label.match(/^(front|first|second|third|fourth)\b/);
            if (namedRow) return positions[namedRow[1]];

            const numberedRow = label.match(/^(\d+)(?:st|nd|rd|th)?\b/);
            return numberedRow ? Number(numberedRow[1]) : Number.MAX_SAFE_INTEGER;
        },

        getSides(parts, row) {
            return this.sortByOrder(parts.reduce((carry, res) => {
                if ( res.row == row && !carry.includes(res.side) ) {
                    carry.push(res.side); 
                }
                return carry;
            }, []), this.getValuesOrder('side'));
        },

        sortByOrder(array, order) {
            return array.sort( (a, b) => {
                let a_index = order.findIndex( word => word == a ); 
                let b_index = order.findIndex( word => word == b ); 
                return a_index - b_index; 
            } ); 
        }, 

        formatPlpLabel(label) {
            if (!label) return '';

            const normalized = label.toString().trim().toLowerCase();
            return normalized.charAt(0).toUpperCase() + normalized.slice(1);
        },

        formatPlpTabLabel(label) {
            const formatted = this.formatPlpLabel(label);
            const configuredRow = formatted.match(/^(Second|Third|Fourth) row\s*-\s*(.+)$/i);

            if (configuredRow) {
                const ordinals = { second: '2nd', third: '3rd', fourth: '4th' };
                return `${ordinals[configuredRow[1].toLowerCase()]} row (${configuredRow[2]})`;
            }

            return formatted
                .replace(/^Second row$/i, '2nd row')
                .replace(/^Third row$/i, '3rd row')
                .replace(/^Fourth row$/i, '4th row');
        },

        setupTabNavigation() {
            this.updateTabNavigation();
            window.addEventListener('resize', this.updateTabNavigation);

            if (window.ResizeObserver && this.$refs.tabsList) {
                this.tabs_resize_observer = new ResizeObserver(this.updateTabNavigation);
                this.tabs_resize_observer.observe(this.$refs.tabsList);
            }
        },

        updateTabNavigation() {
            const tabsList = this.$refs.tabsList;

            if (!tabsList) {
                this.tabs_overflow = false;
                return;
            }

            const maxScrollLeft = Math.max(tabsList.scrollWidth - tabsList.clientWidth, 0);
            const edgeThreshold = 1;

            this.tabs_overflow = maxScrollLeft > edgeThreshold;
        },

        scrollTabs(direction) {
            const tabsList = this.$refs.tabsList;

            if (!tabsList) return;

            const buttons = Array.from(tabsList.querySelectorAll('button'));
            const tabStep = direction === 'right' ? 1 : -1;
            const targetTabIndex = Math.max(
                0,
                Math.min(this.current_tab + tabStep, buttons.length - 1)
            );

            if (targetTabIndex === this.current_tab) return;

            this.current_tab = targetTabIndex;

            const targetButton = buttons[targetTabIndex];
            const maxScrollLeft = Math.max(tabsList.scrollWidth - tabsList.clientWidth, 0);
            const targetScrollLeft = targetButton.offsetLeft
                - (tabsList.clientWidth - targetButton.offsetWidth) / 2;

            tabsList.scrollTo({
                left: Math.max(0, Math.min(targetScrollLeft, maxScrollLeft)),
                behavior: 'smooth'
            });
        },

        formatPlpHeading(row, side) {
            return `${this.formatPlpLabel(row).toUpperCase()} - ${this.formatPlpLabel(side)}`;
        },

        getStickyHeaderOffset() {
            const stickyElements = Array.from(new Set(document.querySelectorAll([
                '#shopify-section-header',
                '.shopify-section-header-sticky',
                'sticky-header',
                '.header-wrapper',
            ].join(','))));

            return stickyElements.reduce((offset, element) => {
                const style = window.getComputedStyle(element);
                const rect = element.getBoundingClientRect();
                const isHeader = element.id === 'shopify-section-header'
                    || element.tagName.toLowerCase() === 'sticky-header'
                    || element.classList.contains('header-wrapper');
                const isSticky = style.position === 'sticky' || style.position === 'fixed';

                if (style.display === 'none' || style.visibility === 'hidden' || rect.height <= 0) {
                    return offset;
                }

                if (isSticky && rect.bottom > 0 && rect.top <= 1) {
                    return Math.max(offset, rect.bottom);
                }

                if (isHeader) {
                    return Math.max(offset, element.offsetHeight || rect.height);
                }

                return offset;
            }, 0);
        },

        getResultSectionScrollTop(target) {
            const offset = this.getStickyHeaderOffset() + 20;

            return Math.max(
                target.getBoundingClientRect().top + window.pageYOffset - offset,
                0
            );
        },

        correctResultSectionScroll(target) {
            const offset = this.getStickyHeaderOffset() + 20;
            const currentTop = target.getBoundingClientRect().top;
            const isHiddenByHeader = currentTop < offset;
            const isTooLow = currentTop > offset + 24;

            if (isHiddenByHeader || isTooLow) {
                window.scrollTo({
                    top: this.getResultSectionScrollTop(target),
                    behavior: 'auto'
                });
            }
        },

        scrollToResultSection(id) {
            const target = document.getElementById(id);

            if (!target) return;

            window.scrollTo({
                top: this.getResultSectionScrollTop(target),
                behavior: 'smooth'
            });

            window.clearTimeout(this.scroll_adjust_timeout);
            this.scroll_adjust_timeout = window.setTimeout(() => {
                this.correctResultSectionScroll(target);
            }, 650);

            if (window.history && window.history.pushState) {
                window.history.pushState(null, null, `#${id}`);
            } else {
                window.location.hash = id;
            }
        },

        /**
         * Filtering based on a selection of filters we create. 
         */
        filterResults( filters ) {

            const results_order = this.getValuesOrder('part'); 
            return this.results.filter (res => {
                let valid = true; 

                // We want to allow null options
                Object.keys(filters).forEach ( key => {
                    if ( res[key] != null && filters[key] != res[key] ) {
                        valid = false; 
                        return false; // break.  
                    }
                }); 

                return valid; 

            }).sort((a, b) => {
                let a_index = results_order.findIndex( value => a.part == value ); 
                let b_index = results_order.findIndex( value => b.part == value ); 
                return a_index - b_index; 

            // We want foam to appear first. 
            }).sort((a, b) => {
                return (
                    !a.part.toLowerCase().includes('foam')
                    && b.part.toLowerCase().includes('foam')
                ) ? 1 : 0;  
            }); 
        }, 

        editSelection() {
            // Removes selections from the URL
            const currentSelections = Storage.getSelections(store.collection);
            const queryString = new URLSearchParams(document.location.search);
            Object.keys(currentSelections).forEach(
                key => queryString.delete(key),
            );
            history.pushState(null, null, '?' + queryString.toString());

            Storage.removeSelections( store.collection ); 
            window.location.reload(); 
        }, 

        async updateProductData() {
            this.pulling_plaform_results = true;

            let products = Storage.getProducts(); 

            if (!products) {
                products = {}; 
            }

            let ids = this.results.map ( result => result.build_id )
                .filter( id => products[id] == null || products[id].tags == null );

            if ( ids.length > 0 ) {
                let {data} = await Api.getProducts( ids ); 

                data.shopify_products.forEach ( product => {
                    products[product.build_id] = Platform.buildProduct( product ); 
                }); 
            }

            // Product data is cached for the session, but status copy is controlled
            // by current theme settings. Recompute it so returning PLP users never
            // keep stale wording or lead times after an admin setting changes.
            if (typeof Platform.getProductStatus === 'function') {
                Object.values(products).forEach(product => {
                    if (!product || product.tags == null) return;
                    product.status = Platform.getProductStatus(product.tags, product.available);
                });
            }

            Storage.storeProducts( products ); 
            store.products = products; 

            this.pulling_plaform_results = false;
        }
    }
}
</script>
